import type { Formation } from "../types/formation";
import type {
  Deck,
  Exercise,
  PedagogicalContentKind,
  PedagogicalContentLists,
  Questionnaire,
} from "../types/pedagogicalContent";
import type {
  FormationImportIssue,
  FormationImportReport,
  FormationTransportBundle,
  FormationTransportDeck,
  FormationTransportExercise,
  FormationTransportFormation,
  FormationTransportObjectType,
  FormationTransportQuestionnaire,
} from "../types/transport";
import {
  createDeck,
  createExercise,
  createQuestionnaire,
  listPedagogicalContent,
} from "./pedagogicalContentService";
import { createFormation } from "./formationService";

function stringField(
  data: Record<string, unknown>,
  key: string,
  fallback = "",
): string {
  return typeof data[key] === "string" ? data[key] : fallback;
}

function stringListField(data: Record<string, unknown>, key: string): string[] {
  return Array.isArray(data[key])
    ? data[key].filter((item): item is string => typeof item === "string")
    : [];
}

function recordField(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function normalizeFormation(value: unknown): FormationTransportFormation {
  const data = recordField(value);

  return {
    description: stringField(data, "description"),
    title: stringField(data, "title", "Formation importée"),
  };
}

function normalizeDeck(value: unknown): FormationTransportDeck {
  const data = recordField(value);
  const pageCount =
    typeof data.pageCount === "number" ? Math.max(0, Math.trunc(data.pageCount)) : 0;

  return {
    draftContent: stringField(data, "draftContent"),
    exerciseRefs: stringListField(data, "exerciseRefs"),
    legacyId: stringField(data, "legacyId"),
    pageCount,
    questionnaireRefs: stringListField(data, "questionnaireRefs"),
    title: stringField(data, "title", "Deck importé"),
  };
}

function normalizeQuestionnaire(value: unknown): FormationTransportQuestionnaire {
  const data = recordField(value);

  return {
    commentedCorrectAnswer: stringField(data, "commentedCorrectAnswer"),
    correctionDeckFragment: stringField(data, "correctionDeckFragment"),
    draftDefinition: stringField(data, "draftDefinition"),
    legacyId: stringField(data, "legacyId"),
    title: stringField(data, "title", "Questionnaire importé"),
  };
}

function normalizeExercise(value: unknown): FormationTransportExercise {
  const data = recordField(value);

  return {
    correctionDeckFragment: stringField(data, "correctionDeckFragment"),
    correctionMarkdown: stringField(data, "correctionMarkdown"),
    draftStatement: stringField(data, "draftStatement"),
    legacyId: stringField(data, "legacyId"),
    responseMode: data.responseMode === "markdown" ? "markdown" : "none",
    title: stringField(data, "title", "Exercice importé"),
  };
}

function normalizeType(value: unknown): FormationTransportObjectType {
  if (
    value === "deck" ||
    value === "questionnaire" ||
    value === "exercise" ||
    value === "formation"
  ) {
    return value;
  }

  return "formation";
}

export function parseFormationTransportBundle(
  input: string,
): FormationTransportBundle {
  const parsed: unknown = JSON.parse(input);
  const data = recordField(parsed);

  return {
    decks: Array.isArray(data.decks) ? data.decks.map(normalizeDeck) : [],
    exercises: Array.isArray(data.exercises)
      ? data.exercises.map(normalizeExercise)
      : [],
    exportVersion: 1,
    exportedAtIso: stringField(data, "exportedAtIso", new Date().toISOString()),
    formation: normalizeFormation(data.formation),
    questionnaires: Array.isArray(data.questionnaires)
      ? data.questionnaires.map(normalizeQuestionnaire)
      : [],
    sourceFormationId:
      typeof data.sourceFormationId === "string" ? data.sourceFormationId : null,
    type: normalizeType(data.type),
  };
}

function issue(
  severity: FormationImportIssue["severity"],
  path: string,
  message: string,
): FormationImportIssue {
  return {
    message,
    path,
    severity,
  };
}

export function validateFormationTransportBundle(
  bundle: FormationTransportBundle,
): FormationImportReport {
  const issues: FormationImportIssue[] = [];
  const ids = new Set<string>();

  if (bundle.exportVersion !== 1) {
    issues.push(issue("error", "exportVersion", "Version d'export non supportée."));
  }

  if (!bundle.formation.title.trim()) {
    issues.push(issue("error", "formation.title", "Titre de formation manquant."));
  }

  for (const [index, deck] of bundle.decks.entries()) {
    if (!deck.legacyId) {
      issues.push(issue("error", `decks.${index}.legacyId`, "ID deck manquant."));
    }

    if (!deck.title.trim()) {
      issues.push(issue("error", `decks.${index}.title`, "Titre deck manquant."));
    }

    ids.add(deck.legacyId);
  }

  for (const [index, questionnaire] of bundle.questionnaires.entries()) {
    if (!questionnaire.legacyId) {
      issues.push(
        issue("error", `questionnaires.${index}.legacyId`, "ID questionnaire manquant."),
      );
    }

    if (!questionnaire.title.trim()) {
      issues.push(
        issue("error", `questionnaires.${index}.title`, "Titre questionnaire manquant."),
      );
    }

    ids.add(questionnaire.legacyId);
  }

  for (const [index, exercise] of bundle.exercises.entries()) {
    if (!exercise.legacyId) {
      issues.push(issue("error", `exercises.${index}.legacyId`, "ID exercice manquant."));
    }

    if (!exercise.title.trim()) {
      issues.push(issue("error", `exercises.${index}.title`, "Titre exercice manquant."));
    }

    ids.add(exercise.legacyId);
  }

  for (const [index, deck] of bundle.decks.entries()) {
    for (const reference of [...deck.questionnaireRefs, ...deck.exerciseRefs]) {
      if (!ids.has(reference)) {
        issues.push(
          issue(
            "warning",
            `decks.${index}.refs`,
            `Référence interne introuvable : ${reference}.`,
          ),
        );
      }
    }
  }

  return {
    createdDeckCount: 0,
    createdExerciseCount: 0,
    createdFormationId: null,
    createdQuestionnaireCount: 0,
    idRemap: {},
    issues,
    status: issues.some((item) => item.severity === "error") ? "invalid" : "valid",
  };
}

function deckToTransport(deck: Deck): FormationTransportDeck {
  return {
    draftContent: deck.draftContent,
    exerciseRefs: deck.exerciseRefs,
    legacyId: deck.id,
    pageCount: deck.pageCount,
    questionnaireRefs: deck.questionnaireRefs,
    title: deck.title,
  };
}

function questionnaireToTransport(
  questionnaire: Questionnaire,
): FormationTransportQuestionnaire {
  return {
    commentedCorrectAnswer: questionnaire.commentedCorrectAnswer,
    correctionDeckFragment: questionnaire.correctionDeckFragment,
    draftDefinition: questionnaire.draftDefinition,
    legacyId: questionnaire.id,
    title: questionnaire.title,
  };
}

function exerciseToTransport(exercise: Exercise): FormationTransportExercise {
  return {
    correctionDeckFragment: exercise.correctionDeckFragment,
    correctionMarkdown: exercise.correctionMarkdown,
    draftStatement: exercise.draftStatement,
    legacyId: exercise.id,
    responseMode: exercise.responseMode,
    title: exercise.title,
  };
}

export async function buildFormationTransportBundle(
  formation: Formation,
): Promise<FormationTransportBundle> {
  const content = await listPedagogicalContent(formation.id);

  return {
    decks: content.decks.map(deckToTransport),
    exercises: content.exercises.map(exerciseToTransport),
    exportVersion: 1,
    exportedAtIso: new Date().toISOString(),
    formation: {
      description: formation.description,
      title: formation.title,
    },
    questionnaires: content.questionnaires.map(questionnaireToTransport),
    sourceFormationId: formation.id,
    type: "formation",
  };
}

export function buildContentObjectTransportBundle({
  content,
  formation,
  kind,
}: {
  content: Deck | Exercise | Questionnaire;
  formation: Formation;
  kind: PedagogicalContentKind;
}): FormationTransportBundle {
  return {
    decks: kind === "deck" ? [deckToTransport(content as Deck)] : [],
    exercises: kind === "exercise" ? [exerciseToTransport(content as Exercise)] : [],
    exportVersion: 1,
    exportedAtIso: new Date().toISOString(),
    formation: {
      description: `Export objet depuis ${formation.title}`,
      title: `${formation.title} - ${content.title}`,
    },
    questionnaires:
      kind === "questionnaire"
        ? [questionnaireToTransport(content as Questionnaire)]
        : [],
    sourceFormationId: formation.id,
    type: kind,
  };
}

function remapReferences(values: string[], idRemap: Record<string, string>): string[] {
  return values
    .map((value) => idRemap[value])
    .filter((value): value is string => typeof value === "string");
}

export async function importFormationTransportBundle({
  actorUid,
  bundle,
}: {
  actorUid: string;
  bundle: FormationTransportBundle;
}): Promise<FormationImportReport> {
  const validation = validateFormationTransportBundle(bundle);

  if (validation.status === "invalid") {
    return validation;
  }

  const formation = await createFormation({
    actorUid,
    draft: {
      description: bundle.formation.description,
      title: `${bundle.formation.title} (import)`,
    },
  });
  const idRemap: Record<string, string> = {};
  let createdQuestionnaireCount = 0;
  let createdExerciseCount = 0;
  let createdDeckCount = 0;

  for (const questionnaire of bundle.questionnaires) {
    const created = await createQuestionnaire({
      actorUid,
      draft: {
        commentedCorrectAnswer: questionnaire.commentedCorrectAnswer,
        correctionDeckFragment: questionnaire.correctionDeckFragment,
        draftDefinition: questionnaire.draftDefinition,
        title: questionnaire.title,
      },
      formationId: formation.id,
    });
    idRemap[questionnaire.legacyId] = created.id;
    createdQuestionnaireCount += 1;
  }

  for (const exercise of bundle.exercises) {
    const created = await createExercise({
      actorUid,
      draft: {
        correctionDeckFragment: exercise.correctionDeckFragment,
        correctionMarkdown: exercise.correctionMarkdown,
        draftStatement: exercise.draftStatement,
        responseMode: exercise.responseMode,
        title: exercise.title,
      },
      formationId: formation.id,
    });
    idRemap[exercise.legacyId] = created.id;
    createdExerciseCount += 1;
  }

  for (const deck of bundle.decks) {
    const created = await createDeck({
      actorUid,
      draft: {
        draftContent: deck.draftContent,
        exerciseRefs: remapReferences(deck.exerciseRefs, idRemap),
        pageCount: deck.pageCount,
        questionnaireRefs: remapReferences(deck.questionnaireRefs, idRemap),
        title: deck.title,
      },
      formationId: formation.id,
    });
    idRemap[deck.legacyId] = created.id;
    createdDeckCount += 1;
  }

  return {
    ...validation,
    createdDeckCount,
    createdExerciseCount,
    createdFormationId: formation.id,
    createdQuestionnaireCount,
    idRemap,
    status: "imported",
  };
}

export function serializeFormationTransportBundle(
  bundle: FormationTransportBundle,
): string {
  return JSON.stringify(bundle, null, 2);
}
